import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { AuthService } from '../interfaces/AuthService';

export class ApiError extends Error {}

export class UnknownApiError extends ApiError {}

export class NotFoundApiError extends ApiError {}

export class BadRequestError extends ApiError {}

export abstract class HttpClient {
    protected readonly urlBase: URL | undefined;

    protected constructor(private readonly authService: AuthService, private readonly baseUrl?: string | URL) {
        if (baseUrl !== undefined && typeof this.baseUrl === 'string') {
            this.urlBase = new URL(baseUrl);
        } else if (baseUrl !== undefined && baseUrl instanceof URL) {
            this.urlBase = baseUrl;
        } else {
            this.urlBase = undefined;
        }
    }

    protected async httpGet<R>(url: string | URL): Promise<AxiosResponse<R>> {
        return this.http('GET', url);
    }

    protected async httpPost<R>(url: string | URL, body?: Record<string, unknown>): Promise<AxiosResponse<R>> {
        return this.http<R>('POST', url, JSON.stringify(body));
    }

    protected async httpPut<R>(url: string | URL, body?: Record<string, unknown>): Promise<AxiosResponse<R>> {
        return this.http<R>('PUT', url, JSON.stringify(body));
    }

    protected async httpDelete<R>(url: string | URL): Promise<AxiosResponse<R>> {
        return this.http<R>('DELETE', url);
    }

    protected async httpPatch<R>(url: string | URL, body?: Record<string, unknown>): Promise<AxiosResponse<R>> {
        return this.http<R>('PATCH', url, JSON.stringify(body));
    }

    private async http<R>(method: string, url: string | URL, data?: string): Promise<AxiosResponse<R>> {
        const finalUrl: URL = new URL(url, this.urlBase);
        const user = await this.authService?.getUser();

        try {
            const request: AxiosRequestConfig = {
                method,
                headers: {
                    Authorization: `${user?.token_type ?? 'Bearer'} ${user?.access_token ?? 'NotFound'}`,
                    ...(data ? { 'content-type': 'application/json' } : undefined)
                },
                url: finalUrl.toString(),
                data
            };
            return await axios.request(request);
        } catch (error: unknown) {
            const error_ = error as AxiosError;
            const statusCode = error_.response?.status.toString();
            const data = error_.response?.data;
            switch (statusCode) {
                case '404': {
                    throw new NotFoundApiError('Not found', { cause: error_ });
                }

                case '400': {
                    throw new BadRequestError(data as string, { cause: error_ });
                }

                case '403': {
                    throw new Error('403');
                }

                default: {
                    throw new UnknownApiError(statusCode, {
                        cause: error_
                    });
                }
            }
        }
    }
}
